import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignaturePad } from "@/components/SignaturePad";
import {
  Plus, ClipboardList, Camera, ImagePlus, X, Check, Loader2, Trash2, Download, ChevronRight,
  ShieldCheck, AlertTriangle, CheckCircle2, CircleAlert, Flag, Sun, CloudRain, AlertOctagon, Hammer, Settings2,
} from "lucide-react";
import { InspectionTemplateManager } from "@/components/inspections/InspectionTemplateManager";

const nzDateTime = (ts) => {
  if (!ts) return "—";
  const parts = new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", day: "2-digit", month: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }).formatToParts(new Date(ts));
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod").toLowerCase()} ${get("timeZoneName")}`.trim();
};
import { toast } from "sonner";

const STATUS_LABEL = {
  draft: "Draft",
  pending_supervisor: "Supervisor sign-off",
  pending_office: "Office review",
  approved: "Approved",
  rejected: "Rejected",
};
const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border-zinc-300 dark:border-zinc-700",
  pending_supervisor: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border-blue-300 dark:border-blue-800",
  pending_office: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300 dark:border-amber-800",
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border-red-300 dark:border-red-800",
};
const CONDITION_OPTIONS = [
  { value: "good", label: "Good — work as planned", icon: Sun, cls: "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200" },
  { value: "fair", label: "Fair — minor issues", icon: CloudRain, cls: "border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200" },
  { value: "poor", label: "Poor — escalate to office", icon: AlertTriangle, cls: "border-orange-600 bg-orange-50 dark:bg-orange-900/30 text-orange-900 dark:text-orange-200" },
  { value: "stop_work", label: "STOP WORK — unsafe", icon: AlertOctagon, cls: "border-red-700 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200" },
];

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold border rounded-sm ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function SiteInspections() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const canManageTemplates = ["admin", "office", "super_admin"].includes(user.role);
  const [tplOpen, setTplOpen] = useState(false);
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [signoffRid, setSignoffRid] = useState(null);
  const [reviewRid, setReviewRid] = useState(null);
  const [editingRid, setEditingRid] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/inspections/records");
      const rows = Array.isArray(data) ? data : [];
      const id = params.get("record");
      if (id && !rows.some(r => r.id === id)) {
        try { const result = await api.get(`/inspections/records/${encodeURIComponent(id)}`); rows.unshift(result.data); }
        catch { toast.error("The linked inspection is unavailable or you do not have access."); }
      }
      setRecords(rows);
    } finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new") === "1") setFormOpen(true); }, [params]);

  useEffect(() => {
    const id = params.get("record");
    if (!isOffice || !id) return;
    let live = true;
    api.get(`/inspections/records/${encodeURIComponent(id)}`).then(({ data }) => {
      if (live && data.status === "pending_office") setReviewRid(id);
    }).catch(() => { if (live) toast.error("The linked inspection is unavailable or you do not have access."); });
    return () => { live = false; };
  }, [params, isOffice]);

  const linkedRecord = params.get("record");
  const filtered = useMemo(() => records.filter(r => (!linkedRecord || r.id === linkedRecord) && (statusFilter === "all" || r.status === statusFilter)), [records, statusFilter, linkedRecord]);
  const counts = useMemo(() => {
    const c = { all: records.length, draft: 0, pending_supervisor: 0, pending_office: 0, approved: 0, rejected: 0 };
    records.forEach((r) => { if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [records]);

  return (
    <div data-testid="inspections-page" className="p-4 lg:p-10 max-w-6xl mx-auto">
      {linkedRecord && <Link to={pathname} className="inline-flex min-h-11 items-center text-sm text-[#FF5F15]">← All inspections</Link>}
      <PageHeader
        tag="/ site walks"
        title="Site inspections"
        subtitle={isOffice
          ? "Daily / weekly / end-of-day site walks from your crews. Review, approve, and any failed item can spawn a hazard into the safety register."
          : "Walk the site, tick the boxes, record the overall condition. Failed items can be sent to the hazard register with one tap."}
        action={
          <div className="flex gap-2">
          {canManageTemplates && (
            <Button variant="outline" data-testid="inspections-templates-btn" onClick={() => setTplOpen(true)} className="rounded-sm font-bold">
              <Settings2 className="w-4 h-4 mr-1.5" /> Templates
            </Button>
          )}
          <Button
            data-testid="inspections-new-btn"
            onClick={() => { setEditingRid(null); setFormOpen(true); }}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New inspection
          </Button>
          </div>
        }
      />

      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-4">
        <TabsList className="grid grid-cols-3 md:flex md:flex-wrap rounded-sm bg-zinc-100 dark:bg-[#151C27] p-1 h-auto">
          <TabsTrigger value="all" data-testid="insp-tab-all" className="rounded-sm text-[12px] font-semibold">All <span className="ml-1.5 text-zinc-500">{counts.all}</span></TabsTrigger>
          <TabsTrigger value="draft" data-testid="insp-tab-draft" className="rounded-sm text-[12px] font-semibold">Draft <span className="ml-1.5 text-zinc-500">{counts.draft}</span></TabsTrigger>
          <TabsTrigger value="pending_supervisor" data-testid="insp-tab-supervisor" className="rounded-sm text-[12px] font-semibold">Supervisor <span className="ml-1.5 text-zinc-500">{counts.pending_supervisor}</span></TabsTrigger>
          <TabsTrigger value="pending_office" data-testid="insp-tab-office" className="rounded-sm text-[12px] font-semibold">Office <span className="ml-1.5 text-zinc-500">{counts.pending_office}</span></TabsTrigger>
          <TabsTrigger value="approved" data-testid="insp-tab-approved" className="rounded-sm text-[12px] font-semibold">Approved <span className="ml-1.5 text-zinc-500">{counts.approved}</span></TabsTrigger>
          <TabsTrigger value="rejected" data-testid="insp-tab-rejected" className="rounded-sm text-[12px] font-semibold">Rejected <span className="ml-1.5 text-zinc-500">{counts.rejected}</span></TabsTrigger>
        </TabsList>
      </Tabs>

      {!loaded ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={records.length === 0 ? "No inspections yet" : "Nothing in this status"}
          description={records.length === 0
            ? (isOffice ? "When your crew walks the site, the records will appear here." : "Start your first site walk now — takes 2 minutes.")
            : "Try a different filter."}
        />
      ) : (
        <div className="space-y-2.5" data-testid="inspections-list">
          {filtered.map((r) => (
            <RecordCard
              key={r.id}
              record={r}
              isOffice={isOffice}
              onSignoff={() => setSignoffRid(r.id)}
              onReview={() => setReviewRid(r.id)}
              onEdit={() => { setEditingRid(r.id); setFormOpen(true); }}
              onPdf={() => downloadPdf(r)}
              onReload={load}
            />
          ))}
        </div>
      )}

      {canManageTemplates && <InspectionTemplateManager open={tplOpen} onOpenChange={setTplOpen} />}
      <FormDialog
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditingRid(null); }}
        editingId={editingRid}
        onSaved={(savedId, autoSignoff) => {
          load();
          if (autoSignoff) setSignoffRid(savedId);
        }}
      />
      <SupervisorSignoffDialog rid={signoffRid} onClose={() => setSignoffRid(null)} onSigned={() => { setSignoffRid(null); load(); }} />
      <OfficeReviewDialog rid={reviewRid} onClose={() => setReviewRid(null)} onReviewed={() => { setReviewRid(null); load(); }} />
    </div>
  );
}

async function downloadPdf(record) {
  try {
    const res = await api.get(`/inspections/records/${record.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inspection-${record.inspection_type}-${record.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF download failed"); }
}

function RecordCard({ record, isOffice, onSignoff, onReview, onEdit, onPdf, onReload }) {
  const canFieldSignoff = record.status === "pending_supervisor";
  const canOfficeReview = isOffice && record.status === "pending_office";
  const canEdit = record.status === "draft" || (record.status === "rejected" && !isOffice);
  const canDelete = (record.status === "draft" && !isOffice) || isOffice;
  const completedAt = nzDateTime(record.submitted_at || record.created_at);
  const cond = CONDITION_OPTIONS.find((c) => c.value === record.condition);
  const hazardCount = (record.checklist_responses || []).filter((r) => r.hazard_id).length;
  const failCount = (record.checklist_responses || []).filter((r) => r.status === "fail").length;

  const del = async () => {
    if (!window.confirm("Delete this inspection?")) return;
    try { await api.delete(`/inspections/records/${record.id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid={`inspection-${record.id}`} className="hard-card p-4 hover:border-[#FF5F15] transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#FF5F15]/10 text-[#FF5F15] shrink-0 rounded-sm">
          <ClipboardList className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-[14px] truncate">{record.template_name}</div>
              <div className="text-[12px] text-zinc-500 dark:text-zinc-400 truncate">
                {record.user_name}
                {record.job_title ? ` · ${record.job_title}` : ""}
                {record.location ? ` · ${record.location}` : ""}
              </div>
              <div className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-widest">{completedAt}</div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <StatusBadge status={record.status} />
              {cond && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${cond.cls}`}>
                  <cond.icon className="w-3 h-3" />
                  {record.condition === "stop_work" ? "STOP" : record.condition}
                </span>
              )}
            </div>
          </div>

          {(failCount > 0 || hazardCount > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
              {failCount > 0 && <span className="text-red-600 dark:text-red-400 font-semibold">⚠ {failCount} fail{failCount === 1 ? "" : "s"}</span>}
              {hazardCount > 0 && <span className="text-orange-600 dark:text-orange-400 font-semibold">⚑ {hazardCount} sent to hazard register</span>}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {canFieldSignoff && (
              <Button size="sm" data-testid={`insp-signoff-${record.id}`} onClick={onSignoff} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Supervisor sign-off
              </Button>
            )}
            {canOfficeReview && (
              <Button size="sm" data-testid={`insp-review-${record.id}`} onClick={onReview} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-black">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Review
              </Button>
            )}
            {canEdit && <Button size="sm" variant="outline" data-testid={`insp-edit-${record.id}`} onClick={onEdit} className="rounded-sm">Edit</Button>}
            <Button size="sm" variant="outline" data-testid={`insp-pdf-${record.id}`} onClick={onPdf} className="rounded-sm">
              <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
            {canDelete && (
              <Button size="sm" variant="ghost" data-testid={`insp-delete-${record.id}`} onClick={del} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {record.status === "rejected" && record.office_review?.notes && (
            <div className="mt-3 p-2.5 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-[12px] text-red-800 dark:text-red-200">
              <div className="font-semibold mb-0.5 flex items-center gap-1.5"><CircleAlert className="w-3.5 h-3.5" /> Office feedback</div>
              {record.office_review.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FormDialog({ open, onOpenChange, editingId, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [record, setRecord] = useState(null);
  const [tplId, setTplId] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("pick");

  useEffect(() => {
    if (!open) return;
    api.get("/inspections/templates").then((r) => setTemplates(Array.isArray(r.data) ? r.data : []));
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : [])).catch(() => setJobs([]));
  }, [open]);

  useEffect(() => {
    if (!open || !editingId) return;
    api.get(`/inspections/records/${editingId}`).then((r) => {
      setRecord(r.data); setTplId(r.data.template_id); setStep("fill");
    });
  }, [open, editingId]);

  useEffect(() => {
    if (!open) { setRecord(null); setTplId(""); setStep("pick"); }
  }, [open]);

  const selectedTpl = useMemo(() => templates.find((t) => t.id === tplId), [templates, tplId]);

  const startFromTemplate = async (tpl) => {
    setSaving(true);
    try {
      const { data } = await api.post("/inspections/records", {
        template_id: tpl.id,
        checklist_responses: tpl.checklist.map((label) => ({ label, status: "pending" })),
        photos: [],
      });
      setRecord(data); setTplId(tpl.id); setStep("fill");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
    finally { setSaving(false); }
  };

  const save = async ({ alsoSubmit = false } = {}) => {
    if (!record) return;
    setSaving(true);
    try {
      const patch = {
        job_id: record.job_id || null,
        location: record.location,
        weather: record.weather,
        condition: record.condition,
        checklist_responses: record.checklist_responses,
        photos: record.photos,
        field_notes: record.field_notes,
      };
      const { data } = await api.patch(`/inspections/records/${record.id}`, patch);
      if (alsoSubmit) {
        const { data: submitted } = await api.post(`/inspections/records/${record.id}/submit`);
        toast.success("Inspection submitted — get a site supervisor to sign off");
        onSaved(submitted.id, true);
        onOpenChange(false);
      } else {
        toast.success("Draft saved");
        onSaved(data.id, false);
        onOpenChange(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="insp-form-dialog">
        <DialogHeader>
          <DialogTitle>{step === "pick" ? "Pick an inspection" : `Site walk · ${record?.template_name || ""}`}</DialogTitle>
          <DialogDescription>
            {step === "pick"
              ? "Choose the type of walk you're doing — daily, weekly, end-of-day, or pick a custom one your office set up."
              : "Tick each item, set the overall site condition, and submit for supervisor sign-off. Any failed item can be sent to the hazard register."}
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-1.5" data-testid="insp-template-picker">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`insp-tpl-${t.inspection_type}`}
                disabled={saving}
                onClick={() => startFromTemplate(t)}
                className="w-full text-left p-3 rounded-sm border border-zinc-200 dark:border-zinc-800 hover:border-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors flex items-center gap-3 disabled:opacity-50"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#FF5F15]/10 text-[#FF5F15] rounded-sm shrink-0">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13.5px]">{t.name}</div>
                  <div className="text-[11.5px] text-zinc-500 dark:text-zinc-400">
                    {t.checklist.length} checks
                    {t.photos_required ? " · 📷 photos required" : ""}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
            {templates.length === 0 && <div className="text-center py-6 text-sm text-zinc-500">No templates available.</div>}
          </div>
        )}

        {step === "fill" && record && selectedTpl && (
          <FormBody record={record} template={selectedTpl} jobs={jobs} onChange={setRecord} />
        )}

        {step === "fill" && (
          <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white dark:bg-[#0B1018] border-t border-zinc-200 dark:border-zinc-800 flex-col sm:flex-row gap-2">
            <Button variant="outline" data-testid="insp-save-draft" onClick={() => save({ alsoSubmit: false })} disabled={saving} className="rounded-sm w-full sm:w-auto">Save draft</Button>
            <Button data-testid="insp-submit-btn" onClick={() => save({ alsoSubmit: true })} disabled={saving} className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold w-full sm:w-auto">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Submit for sign-off
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormBody({ record, template, jobs, onChange }) {
  const set = (patch) => onChange({ ...record, ...patch });
  const setItem = (idx, patch) => {
    const copy = [...record.checklist_responses];
    copy[idx] = { ...copy[idx], ...patch };
    set({ checklist_responses: copy });
  };
  const decidedCount = (record.checklist_responses || []).filter((r) => r.status !== "pending").length;
  const totalCount = (record.checklist_responses || []).length;

  const uploadPhoto = async (file) => {
    if (!file) return null;
    const fd = new FormData(); fd.append("file", file); fd.append("category", "inspection");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.storage_path || data.path || data.file_path || null;
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo upload failed"); return null; }
  };
  const uploadPhotos = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const paths = [];
    for (const f of list) {
      const p = await uploadPhoto(f);
      if (p) paths.push(p);
    }
    if (paths.length) set({ photos: [...(record.photos || []), ...paths] });
  };
  const removePhoto = (i) => set({ photos: (record.photos || []).filter((_, idx) => idx !== i) });

  const spawnHazard = async (idx) => {
    const severity = window.prompt("Hazard severity? (low / medium / high / critical)", "medium");
    if (!severity || !["low", "medium", "high", "critical"].includes(severity.toLowerCase())) {
      toast.message("Cancelled — severity must be low/medium/high/critical");
      return;
    }
    try {
      const { data } = await api.post(`/inspections/records/${record.id}/spawn-hazard`, {
        item_index: idx, severity: severity.toLowerCase(),
      });
      setItem(idx, { hazard_id: data.hazard_id });
      toast.success("Sent to hazard register");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not spawn hazard");
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Job (optional)</Label>
          <Select value={record.job_id || "__none__"} onValueChange={(v) => set({ job_id: v === "__none__" ? null : v })}>
            <SelectTrigger data-testid="insp-job-select" className="rounded-sm mt-1"><SelectValue placeholder="Select a job…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} · ` : ""}{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Location on site</Label>
          <Input data-testid="insp-location" value={record.location || ""} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Lot 14, west boundary" className="rounded-sm mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Weather / conditions</Label>
          <Input data-testid="insp-weather" value={record.weather || ""} onChange={(e) => set({ weather: e.target.value })} placeholder="e.g. Fine, 18°C, light wind" className="rounded-sm mt-1" />
        </div>
      </div>

      {/* Overall site condition */}
      <div>
        <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400 mb-2">
          <span className="opacity-50">//</span> Overall site condition <span className="text-red-500">*</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="insp-condition-picker">
          {CONDITION_OPTIONS.map((c) => {
            const active = record.condition === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => set({ condition: c.value })}
                data-testid={`insp-cond-${c.value}`}
                className={`p-3 rounded-sm border-2 text-left transition-all ${
                  active ? c.cls : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 text-zinc-700 dark:text-zinc-300"
                }`}
              >
                <c.icon className="w-4 h-4 mb-1" />
                <div className="text-[11px] font-bold uppercase tracking-wider">{c.value.replace("_", " ")}</div>
                <div className="text-[10px] mt-0.5">{c.label.split(" — ")[1] || c.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
            <span className="opacity-50">//</span> Checklist
          </div>
          <div className="text-[11px] font-mono text-zinc-500">{decidedCount}/{totalCount} decided</div>
        </div>
        <div className="space-y-1.5" data-testid="insp-checklist">
          {(record.checklist_responses || []).map((r, i) => (
            <ChecklistRow key={i} row={r} testIdx={i} onChange={(patch) => setItem(i, patch)} onSpawnHazard={() => spawnHazard(i)} />
          ))}
        </div>
      </div>

      {/* Photos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
            <span className="opacity-50">//</span> Photos {template.photos_required && <span className="text-red-500 ml-1">*</span>}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[11px] font-semibold text-[#FF5F15] cursor-pointer hover:underline" data-testid="insp-photo-camera-label">
              <input type="file" accept="image/*" capture="environment" data-testid="insp-photo-input" onChange={(e) => uploadPhotos(e.target.files)} className="hidden" />
              <Camera className="w-3.5 h-3.5 inline mr-1" /> Camera
            </label>
            <label className="text-[11px] font-semibold text-[#FF5F15] cursor-pointer hover:underline" data-testid="insp-photo-gallery-label">
              <input type="file" accept="image/*" multiple data-testid="insp-photo-gallery-input" onChange={(e) => uploadPhotos(e.target.files)} className="hidden" />
              <ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Gallery
            </label>
          </div>
        </div>
        {template.photos_required && (record.photos || []).length === 0 && (
          <div className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> At least one photo is required to submit.
          </div>
        )}
        {(record.photos || []).length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {record.photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid={`insp-photo-${i}`}>
                <img
                  src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`}
                  alt={`Photo ${i + 1}`} className="w-full h-full object-cover"
                />
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-sm flex items-center justify-center hover:bg-red-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Notes (optional)</Label>
        <Textarea data-testid="insp-field-notes" value={record.field_notes || ""} onChange={(e) => set({ field_notes: e.target.value })} placeholder="Anything else worth flagging…" className="rounded-sm mt-1 min-h-20" />
      </div>
    </div>
  );
}

function ChecklistRow({ row, testIdx, onChange, onSpawnHazard }) {
  return (
    <div className={`p-2.5 rounded-sm border ${
      row.status === "pass" ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800"
      : row.status === "fail" ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800"
      : row.status === "na" ? "border-zinc-300 bg-zinc-50 dark:bg-[#151C27] dark:border-zinc-700"
      : "border-zinc-200 dark:border-zinc-800"
    }`}>
      <div className="flex items-start gap-2.5">
        <div className="flex-1 text-[13.5px] font-medium">{row.label}</div>
        <div className="flex gap-1 shrink-0">
          {[
            { v: "pass", l: "Pass", cls: "bg-emerald-600 text-white hover:bg-emerald-700" },
            { v: "fail", l: "Fail", cls: "bg-red-600 text-white hover:bg-red-700" },
            { v: "na", l: "N/A", cls: "bg-zinc-500 text-white hover:bg-zinc-600" },
          ].map((b) => (
            <button
              key={b.v}
              type="button"
              data-testid={`insp-check-${testIdx}-${b.v}`}
              onClick={() => onChange({ status: b.v })}
              className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-sm transition-all ${
                row.status === b.v ? b.cls : "bg-white dark:bg-[#151C27] text-zinc-700 dark:text-zinc-300 border border-zinc-300 dark:border-zinc-700 hover:border-zinc-500"
              }`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>
      {row.status === "fail" && (
        <div className="mt-2 space-y-1.5">
          <Input
            value={row.notes || ""}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="What did you find? (required for fail)"
            className="rounded-sm text-[12.5px] h-8"
            data-testid={`insp-check-notes-${testIdx}`}
          />
          {row.hazard_id ? (
            <div className="text-[11px] text-orange-600 dark:text-orange-400 font-semibold flex items-center gap-1">
              <Flag className="w-3 h-3" /> Sent to hazard register
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onSpawnHazard}
              data-testid={`insp-spawn-hazard-${testIdx}`}
              className="rounded-sm h-7 text-[11px] border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            >
              <Flag className="w-3 h-3 mr-1" /> Send to hazard register
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function SupervisorSignoffDialog({ rid, onClose, onSigned }) {
  const [supName, setSupName] = useState("");
  const [sig, setSig] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (rid) { setSupName(""); setSig(null); setNotes(""); } }, [rid]);

  const submit = async () => {
    if (!supName.trim()) { toast.error("Supervisor name required"); return; }
    if (!sig) { toast.error("Signature required"); return; }
    setSaving(true);
    try {
      await api.post(`/inspections/records/${rid}/supervisor-signoff`, {
        supervisor_name: supName.trim(), signature: sig, notes: notes.trim() || null,
      });
      toast.success("Sign-off recorded — sent to office for review");
      onSigned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign-off failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="insp-signoff-dialog">
        <DialogHeader>
          <DialogTitle>Site supervisor sign-off</DialogTitle>
          <DialogDescription>Pass the phone to your site supervisor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Supervisor name</Label>
            <Input data-testid="insp-supervisor-name" value={supName} onChange={(e) => setSupName(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Signature</Label>
            <div className="mt-1 border border-zinc-300 dark:border-zinc-700 rounded-sm overflow-hidden">
              <SignaturePad onSave={setSig} onCancel={() => setSig(null)} testIdPrefix="insp-sig" />
            </div>
            {sig && <div className="text-[11px] text-emerald-600 mt-1">✓ Signature captured</div>}
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Notes (optional)</Label>
            <Textarea data-testid="insp-supervisor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1 min-h-16" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="insp-signoff-submit" onClick={submit} disabled={saving} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
            Sign off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfficeReviewDialog({ rid, onClose, onReviewed }) {
  const [record, setRecord] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!rid) { setRecord(null); setNotes(""); return; }
    api.get(`/inspections/records/${rid}`).then((r) => setRecord(r.data));
  }, [rid]);

  const decide = async (decision) => {
    setSaving(true);
    try {
      await api.post(`/inspections/records/${rid}/review`, { decision, notes: notes.trim() || null });
      toast.success(decision === "approved" ? "Inspection approved" : "Rejected — sent back for amendment");
      onReviewed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="insp-review-dialog">
        <DialogHeader>
          <DialogTitle>Review · {record?.template_name || ""}</DialogTitle>
          <DialogDescription>
            Submitted by {record?.user_name} on {nzDateTime(record?.submitted_at)} · signed off by {record?.supervisor_signoff?.supervisor_name || "—"}
          </DialogDescription>
        </DialogHeader>
        {record ? (
          <div className="space-y-4 text-[13px]">
            {record.condition && (
              <div className={`p-3 rounded-sm border-2 ${(CONDITION_OPTIONS.find(c => c.value === record.condition) || {}).cls}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest">Overall condition</div>
                <div className="font-display font-black text-lg uppercase mt-0.5">{record.condition.replace("_", " ")}</div>
              </div>
            )}
            <ReviewSummary record={record} />
            <div>
              <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Office notes (shown to field worker if rejected)</Label>
              <Textarea data-testid="insp-review-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1 min-h-20" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={() => decide("rejected")} disabled={saving} variant="destructive" className="rounded-sm" data-testid="insp-review-reject"><X className="w-4 h-4 mr-1.5" /> Reject</Button>
          <Button onClick={() => decide("approved")} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="insp-review-approve"><Check className="w-4 h-4 mr-1.5" /> Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewSummary({ record }) {
  const failCount = (record.checklist_responses || []).filter((r) => r.status === "fail").length;
  const passCount = (record.checklist_responses || []).filter((r) => r.status === "pass").length;
  const naCount = (record.checklist_responses || []).filter((r) => r.status === "na").length;
  const hazardCount = (record.checklist_responses || []).filter((r) => r.hazard_id).length;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat n={passCount} l="Pass" cls="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300" />
        <Stat n={failCount} l="Fail" cls={failCount > 0 ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300" : "bg-zinc-50 dark:bg-[#151C27] border-zinc-200 dark:border-zinc-800 text-zinc-500"} />
        <Stat n={naCount} l="N/A" cls="bg-zinc-50 dark:bg-[#151C27] border-zinc-200 dark:border-zinc-800 text-zinc-500" />
        <Stat n={hazardCount} l="Hazards" cls={hazardCount > 0 ? "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300" : "bg-zinc-50 dark:bg-[#151C27] border-zinc-200 dark:border-zinc-800 text-zinc-500"} />
      </div>
      {failCount > 0 && (
        <div className="p-2.5 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-[12px] text-red-800 dark:text-red-200">
          <div className="font-semibold mb-1">Failed items:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {record.checklist_responses.filter((r) => r.status === "fail").map((r, i) => (
              <li key={i}>{r.label}{r.notes ? ` — ${r.notes}` : ""}{r.hazard_id ? " (→ hazard register)" : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {record.field_notes && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Field notes</div>
          <div className="text-[12.5px] p-2.5 bg-zinc-50 dark:bg-[#151C27] rounded-sm border border-zinc-200 dark:border-zinc-800">{record.field_notes}</div>
        </div>
      )}
      {(record.photos || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Photos ({record.photos.length})</div>
          <div className="grid grid-cols-4 gap-1.5">
            {record.photos.map((p, i) => (
              <img key={i} src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`} alt={`Photo ${i + 1}`} className="aspect-square w-full object-cover rounded-sm border border-zinc-200 dark:border-zinc-800" />
            ))}
          </div>
        </div>
      )}
      {record.supervisor_signoff?.signature && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Supervisor signature · {record.supervisor_signoff.supervisor_name}</div>
          <img src={record.supervisor_signoff.signature} alt="Signature" className="bg-white border border-zinc-200 dark:border-zinc-800 rounded-sm max-h-24" />
        </div>
      )}
    </div>
  );
}

function Stat({ n, l, cls }) {
  return (
    <div className={`p-2 border rounded-sm ${cls}`}>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-[10px] uppercase tracking-widest">{l}</div>
    </div>
  );
}
